import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertCircle, Check, Clock, X } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../ui/Button';

const ModeSwitchOTPModal = ({ isOpen, onClose, onSuccess, targetMode }) => {
    const { updateUser } = useAuth();
    const [step, setStep] = useState('requesting'); // 'requesting', 'verifying', 'error', 'success'
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [blockDetails, setBlockDetails] = useState(null);
    const [countdown, setCountdown] = useState(60);

    useEffect(() => {
        if (isOpen && targetMode) {
            startFlow();
        } else {
            // Reset state when closed
            setStep('requesting');
            setOtp('');
            setErrorMsg('');
            setBlockDetails(null);
            setCountdown(60);
        }
    }, [isOpen, targetMode]);

    useEffect(() => {
        let timer;
        if (step === 'verifying' && countdown > 0) {
            timer = setInterval(() => setCountdown(c => c - 1), 1000);
        }
        return () => clearInterval(timer);
    }, [step, countdown]);

    const startFlow = async () => {
        setLoading(true);
        setErrorMsg('');
        setBlockDetails(null);
        setStep('requesting');

        try {
            const response = await api.post('/users/switch-mode/request-otp');

            if (response.data && response.data.success) {
                setStep('verifying');
                setCountdown(60);
            } else {
                handleError(response.data);
            }
        } catch (error) {
            handleError(error.response?.data || { message: 'Network error requesting verification code' });
        } finally {
            setLoading(false);
        }
    };

    const verifyAndSwitch = async () => {
        if (!otp || otp.length < 6) {
            setErrorMsg('Please enter the 6-digit code');
            return;
        }

        setLoading(true);
        setErrorMsg('');

        try {
            const response = await api.post('/users/switch-mode', {
                newMode: targetMode,
                otp: otp
            });

            if (response.data && response.data.success) {
                setStep('success');

                // Update local storage synchronously
                const cachedUser = JSON.parse(localStorage.getItem('gigship_user') || '{}');
                cachedUser.activeRole = targetMode;
                localStorage.setItem('gigship_user', JSON.stringify(cachedUser));

                // Update context
                updateUser({ activeRole: targetMode });

                if (onSuccess) {
                    setTimeout(() => {
                        onSuccess();
                    }, 1500); // Give user time to read success message
                } else {
                    setTimeout(() => {
                        onClose();
                    }, 1500);
                }
            } else {
                handleError(response.data);
            }
        } catch (error) {
            handleError(error.response?.data || { message: 'Failed to verify code and switch mode' });
        } finally {
            setLoading(false);
        }
    };

    const handleError = (data) => {
        setStep('error');
        setErrorMsg(data.message || 'An unexpected error occurred');
        if (data.details || data.reason) {
            setBlockDetails({ reason: data.reason, details: data.details });
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                    disabled={loading}
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="flex items-center gap-3 mb-6">
                    <RefreshCw className={`w-6 h-6 text-blue-600 ${loading && step === 'requesting' ? 'animate-spin' : ''}`} />
                    <h3 className="text-xl font-bold text-gray-900 capitalize">
                        Switch to {targetMode} Mode
                    </h3>
                </div>

                {step === 'requesting' && (
                    <div className="text-center py-8">
                        {loading ? (
                            <>
                                <div className="mx-auto w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4" />
                                <p className="text-gray-600">Checking eligibility and sending verification code...</p>
                            </>
                        ) : (
                            <p className="text-gray-600">Preparing to send verification code...</p>
                        )}
                    </div>
                )}

                {step === 'verifying' && (
                    <div className="space-y-6">
                        <div className="text-center">
                            <p className="text-sm text-gray-600 mb-2">
                                We've sent a 6-digit verification code to your email.
                            </p>
                            <p className="text-xs text-gray-500">
                                This ensures the security of your account before switching modes.
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
                                Enter Verification Code
                            </label>
                            <input
                                type="text"
                                value={otp}
                                onChange={(e) => {
                                    setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                                    setErrorMsg('');
                                }}
                                placeholder="000000"
                                className="w-full text-center text-3xl tracking-[0.5em] font-mono p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                                maxLength={6}
                                autoFocus
                            />
                        </div>

                        <div className="flex flex-col gap-3">
                            <Button
                                onClick={verifyAndSwitch}
                                variant="primary"
                                className="w-full"
                                loading={loading}
                                disabled={otp.length < 6 || loading}
                            >
                                Verify & Switch Mode
                            </Button>

                            <button
                                onClick={startFlow}
                                disabled={countdown > 0 || loading}
                                className="text-sm text-center font-medium text-blue-600 disabled:text-gray-400 hover:text-blue-800 transition-colors"
                            >
                                {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend verification code'}
                            </button>
                        </div>
                    </div>
                )}

                {step === 'error' && (
                    <div className="space-y-6">
                        <div className="flex flex-col items-center justify-center py-4">
                            <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                            <h4 className="text-lg font-semibold text-gray-900 mb-2">Cannot Switch Mode</h4>
                            <p className="text-center text-gray-600 mb-4">{errorMsg}</p>

                            {/* Show detailed blocker info if available */}
                            {blockDetails?.reason === 'cooldown' && blockDetails.details?.nextAvailable && (
                                <div className="bg-yellow-50 text-yellow-800 p-4 rounded-lg text-sm w-full flex items-start gap-3">
                                    <Clock className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-600" />
                                    <div>
                                        <p className="font-semibold mb-1">Cooldown Active</p>
                                        <p>You can switch modes again after:</p>
                                        <p className="font-medium mt-1">
                                            {new Date(blockDetails.details.nextAvailable).toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {blockDetails?.reason === 'active_orders_gigger' && (
                                <div className="bg-yellow-50 text-yellow-800 p-4 rounded-lg text-sm w-full">
                                    <p className="font-semibold mb-1">Action Required</p>
                                    <p>You have <strong>{blockDetails.details.count}</strong> active order(s) as a Gigger. Please complete or cancel them before switching to Shipper mode.</p>
                                </div>
                            )}

                            {blockDetails?.reason === 'active_orders_shipper' && (
                                <div className="bg-yellow-50 text-yellow-800 p-4 rounded-lg text-sm w-full">
                                    <p className="font-semibold mb-1">Action Required</p>
                                    <p>You have <strong>{blockDetails.details.count}</strong> active order(s) as a Shipper. Please complete payment or cancel them before switching to Gigger mode.</p>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-3">
                            <Button onClick={onClose} variant="secondary" className="flex-1">
                                Cancel
                            </Button>
                            <Button onClick={startFlow} variant="primary" className="flex-1">
                                Try Again
                            </Button>
                        </div>
                    </div>
                )}

                {step === 'success' && (
                    <div className="flex flex-col items-center justify-center py-8">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                            <Check className="w-8 h-8 text-green-600" />
                        </div>
                        <h4 className="text-xl font-bold text-gray-900 mb-2">Switch Successful!</h4>
                        <p className="text-center text-gray-600">
                            You are now operating in <strong className="capitalize">{targetMode}</strong> mode.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ModeSwitchOTPModal;
